<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {conversationApi} from "@/container";
import {
    ConversationMessage,
    ConversationMessageSourceOption,
    ConversationMessageTriggerType
} from "@/conversation/interfaces";
import ConversationMessagesTable from "@/conversation/components/ConversationMessagesTable.vue";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {Switch} from "@/core/components/ui/switch";
import {Label} from "@/core/components/ui/label";
import {AcceptableValue} from "reka-ui";
import {TRIGGER_TYPE_OPTIONS} from "@/conversation/services/triggerType";

const ALL_SOURCES = "all";
const ALL_TRIGGERS = "all";

const messages = ref<ConversationMessage[]>();
const loading = ref(false);
const total = ref(0);
const page = ref(1);
const limit = ref(20);
const sources = ref<ConversationMessageSourceOption[]>([]);
const selectedSourceKey = ref<string>(ALL_SOURCES);
const selectedTrigger = ref<string>(ALL_TRIGGERS);
const withInteractionOnly = ref(false);

const sourceKey = (source: ConversationMessageSourceOption) => `${source.type}:${source.id}`;

const selectedSource = computed(() => {
    return sources.value.find((source) => sourceKey(source) === selectedSourceKey.value);
});

const fetchMessages = () => {
    loading.value = true;

    conversationApi().conversationMessages({
        page: page.value,
        limit: limit.value,
        with: ['sourceable'],
        ...(selectedSource.value ? {
            sourceable_type: selectedSource.value.type,
            sourceable_id: selectedSource.value.id
        } : {}),
        ...(selectedTrigger.value !== ALL_TRIGGERS ? {
            trigger_type: selectedTrigger.value as ConversationMessageTriggerType
        } : {}),
        ...(withInteractionOnly.value ? {has_interaction: 1 as const} : {})
    })
        .then(response => {
            messages.value = response.data;
            total.value = response.meta?.total ?? 0;
        })
        .finally(() => {
            loading.value = false;
        });
};

const fetchSources = () => {
    conversationApi().conversationMessageSources()
        .then(response => {
            sources.value = response.data;
        });
};

const handleSourceChange = (value: AcceptableValue) => {
    selectedSourceKey.value = typeof value === "string" ? value : ALL_SOURCES;
    page.value = 1;
    fetchMessages();
};

const handleTriggerChange = (value: AcceptableValue) => {
    selectedTrigger.value = typeof value === "string" ? value : ALL_TRIGGERS;
    page.value = 1;
    fetchMessages();
};

const handleInteractionFilterChange = (value: boolean) => {
    withInteractionOnly.value = value;
    page.value = 1;
    fetchMessages();
};

const handlePageChange = (newPage: number) => {
    if (newPage === page.value) {
        return;
    }

    page.value = newPage;
    fetchMessages();
};

const handleLimitChange = (newLimit: number) => {
    if (newLimit === limit.value) {
        return;
    }

    page.value = 1;
    limit.value = newLimit;
    fetchMessages();
};

onMounted(() => {
    fetchMessages();
    fetchSources();
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-4">
      <Select :model-value="selectedSourceKey" @update:model-value="handleSourceChange">
        <SelectTrigger class="w-[200px] bg-white">
          <SelectValue placeholder="Filtrer par contexte" />
        </SelectTrigger>
        <SelectContent class="max-h-[300px]">
          <SelectItem :value="ALL_SOURCES">
            Tous les contextes
          </SelectItem>
          <SelectItem v-for="source in sources" :key="sourceKey(source)" :value="sourceKey(source)">
            {{ source.name }}
          </SelectItem>
        </SelectContent>
      </Select>
      <Select :model-value="selectedTrigger" @update:model-value="handleTriggerChange">
        <SelectTrigger class="w-[200px] bg-white">
          <SelectValue placeholder="Filtrer par type d'envoi" />
        </SelectTrigger>
        <SelectContent class="max-h-[300px]">
          <SelectItem :value="ALL_TRIGGERS">
            Tous les types d'envoi
          </SelectItem>
          <SelectItem v-for="option in TRIGGER_TYPE_OPTIONS" :key="option.value" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
      <div class="flex items-center ml-auto gap-2">
        <Switch
          id="with-interaction-only"
          :model-value="withInteractionOnly"
          @update:model-value="handleInteractionFilterChange"
        />
        <Label for="with-interaction-only" class="cursor-pointer">
          Avec interaction uniquement
        </Label>
      </div>
    </div>
    <div class="shadow-md rounded-lg">
      <ConversationMessagesTable
        :limits="limit"
        :messages="messages ?? []"
        :total="total"
        :loading="loading"
        :extends="['flow', 'sent_at', 'preview', 'title']"
        @update:page="handlePageChange"
        @update:limits="handleLimitChange"
      />
    </div>
  </div>
</template>
